import { memo, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { htmlEncode } from 'js-htmlencode';
import { chartGlobalOptions, createChartsTooltip, lineChartOptions, rotatedXAxisLabelsOptions } from '../../chart';
import { baseDarkColour, rainbowPalette } from '../../palette';
import { currencyFormat, decimalFormat, percentFormat, translate } from '../../utils';

const OVERVIEW_METRICS = [
    { metricKey: 'occupancy', labelTranslationKey: 'PORTFOLIO_DASHBOARD_OCCUPANCY', valueFormat: 'percent' },
    { metricKey: 'revenue', labelTranslationKey: 'PORTFOLIO_DASHBOARD_REVENUE', valueFormat: 'currency' },
    { metricKey: 'adr', labelTranslationKey: 'PORTFOLIO_DASHBOARD_ADR', valueFormat: 'currency' },
    { metricKey: 'revPAR', labelTranslationKey: 'PORTFOLIO_DASHBOARD_REVPAR', valueFormat: 'currency' },
    { metricKey: 'occupiedRooms', labelTranslationKey: 'PORTFOLIO_DASHBOARD_OCCUPIED_ROOMS', valueFormat: 'integer' },
];

const PAST_OVERVIEW_STROKE_DASH_ARRAY = 4;

// ApexCharts adds labels.offsetX twice when placing a category label, and the x-annotation copies that label x.
const SELECTED_DATE_LINE_OFFSET_X = -2 * rotatedXAxisLabelsOptions.labels.offsetX;

/** @param {import('./portfolioDashboard.types').PortfolioOverviewProps} props */
export const PortfolioOverview = memo(function PortfolioOverview({ overview, metricKey, onMetricChange }) {
    const [chartKey, setChartKey] = useState(0);
    const [chartInputs, setChartInputs] = useState({ overview, metricKey });

    // Recreate the chart instead of updating it: on update ApexCharts re-maps legend-hidden series by name,
    // which breaks the dashed/solid pairing.
    if (chartInputs.overview !== overview || chartInputs.metricKey !== metricKey) {
        setChartInputs({ overview, metricKey });
        setChartKey(chartKey + 1);
    }

    const { series, options } = useMemo(() => {
        const categories = overview.dates.map((date) => moment(date).format('MMM Do'));
        const selectedDateIndex = overview.dates.indexOf(overview.selectedDate);
        const propertyColors = rainbowPalette(overview.properties.length);
        const selectedOverviewMetric = OVERVIEW_METRICS.find((overviewMetric) => overviewMetric.metricKey === metricKey);
        const isPercent = selectedOverviewMetric.valueFormat === 'percent';
        const isInteger = selectedOverviewMetric.valueFormat === 'integer';
        const series = buildOverviewChartSeries(overview.properties, metricKey, isPercent, selectedDateIndex);
        const seriesColors = propertyColors.flatMap((propertyColor) => [propertyColor, propertyColor]);

        const options = {
            ...chartGlobalOptions,
            chart: {
                ...chartGlobalOptions.chart,
                animations: {
                    enabled: false,
                },
                events: {
                    mounted: (mountedChart) => {
                        hidePastOverviewLegendItems(mountedChart);
                        mountedChart.el.addEventListener('mousemove', (event) => {
                            revealPairedOverviewSeries(mountedChart, event);
                        });
                    },
                    updated: hidePastOverviewLegendItems,
                    legendClick: togglePairedOverviewSeries,
                },
            },
            colors: seriesColors,
            legend: {
                ...chartGlobalOptions.legend,
                // ApexCharts writes legend text with innerHTML.
                formatter: (seriesName) => htmlEncode(seriesName),
            },
            xaxis: {
                ...rotatedXAxisLabelsOptions,
                categories,
                tickAmount: 20,
            },
            yaxis: isPercent
                ? {
                    min: 0,
                    max: 100,
                    labels: {
                        formatter: (value) => `${value}%`,
                    },
                }
                : {
                    min: 0,
                    labels: {
                        formatter: (value) => (isInteger ? decimalFormat(value, 0) : currencyFormat(value)),
                    },
                },
            annotations: {
                xaxis: [{
                    x: categories[selectedDateIndex],
                    borderColor: baseDarkColour,
                    strokeDashArray: 0,
                    offsetX: SELECTED_DATE_LINE_OFFSET_X,
                    label: {
                        text: translate(
                            'frontend',
                            overview.selectedDate === overview.today
                                ? 'PORTFOLIO_DASHBOARD_OVERVIEW_TODAY'
                                : 'PORTFOLIO_DASHBOARD_OVERVIEW_SELECTED_DATE',
                        ),
                        orientation: 'horizontal',
                        offsetX: SELECTED_DATE_LINE_OFFSET_X,
                        borderColor: baseDarkColour,
                        style: {
                            color: baseDarkColour,
                        },
                    },
                }],
            },
            tooltip: {
                ...chartGlobalOptions.tooltip,
                custom: ({ dataPointIndex, w }) => createChartsTooltip(
                    categories[dataPointIndex],
                    overview.properties.flatMap((propertyOverview, propertyIndex) => {
                        const futureSeriesIndex = propertyIndex * 2 + 1;
                        if (w.globals.collapsedSeriesIndices.includes(futureSeriesIndex)) return [];

                        const metricValue = propertyOverview[metricKey][dataPointIndex];

                        return [{
                            name: propertyOverview.name,
                            value: isPercent
                                ? percentFormat(metricValue, 1)
                                : isInteger
                                    ? decimalFormat(metricValue, 0)
                                    : currencyFormat(metricValue),
                            color: propertyColors[propertyIndex],
                        }];
                    }),
                ),
            },
            markers: {
                ...lineChartOptions.markers,
                size: 0,
            },
            stroke: {
                ...lineChartOptions.stroke,
                dashArray: overview.properties.flatMap(() => [PAST_OVERVIEW_STROKE_DASH_ARRAY, 0]),
            },
        };

        return { series, options };
    }, [overview, metricKey]);

    const handleMetricClick = (event) => {
        event.preventDefault();

        const selectedMetricKey = event.currentTarget.dataset.metricKey;
        if (selectedMetricKey === metricKey) return;

        onMetricChange(selectedMetricKey);
    };

    return (
        <div className="box box-solid mb-0">
            <div className="box-header">
                <h3 className="box-title">
                    {translate('frontend', 'PORTFOLIO_DASHBOARD_OVERVIEW')}
                </h3>

                <div className="box-tools">
                    <ul className="nav nav-pills">
                        {OVERVIEW_METRICS.map((overviewMetric) => (
                            <li
                                key={overviewMetric.metricKey}
                                className={metricKey === overviewMetric.metricKey ? 'active' : undefined}
                            >
                                <a href="#" data-metric-key={overviewMetric.metricKey} onClick={handleMetricClick}>
                                    {translate('frontend', overviewMetric.labelTranslationKey)}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="box-body">
                <Chart key={chartKey} type="line" height={360} series={series} options={options} />
            </div>
        </div>
    );
});

/**
 * @param {import('./portfolioDashboard.types').PortfolioOverviewProperty[]} properties
 * @param {string} metricKey
 * @param {boolean} isPercent
 * @param {number} selectedDateIndex
 * @returns {{ name: string, data: (number | null)[] }[]}
 */
function buildOverviewChartSeries(properties, metricKey, isPercent, selectedDateIndex) {
    // ApexCharts dashes a whole series, so each property is a dashed series through the selected date and a solid series from it.
    return properties.flatMap((propertyOverview) => {
        const metricValues = isPercent
            ? propertyOverview[metricKey].map((value) => value * 100)
            : propertyOverview[metricKey];

        return [
            {
                name: propertyOverview.name,
                data: metricValues.map((value, pointIndex) => (pointIndex <= selectedDateIndex ? value : null)),
            },
            {
                name: propertyOverview.name,
                data: metricValues.map((value, pointIndex) => (pointIndex >= selectedDateIndex ? value : null)),
            },
        ];
    });
}

/**
 * @param {import('apexcharts')} chart
 */
function hidePastOverviewLegendItems(chart) {
    const legendItems = chart.el.querySelectorAll('.apexcharts-legend-series');

    legendItems.forEach((legendItem) => {
        const seriesIndex = Number(legendItem.getAttribute('rel')) - 1;
        if (seriesIndex % 2 !== 0) return;

        legendItem.style.display = 'none';
    });
}

/**
 * @param {import('apexcharts')} chart
 * @param {MouseEvent} event
 */
function revealPairedOverviewSeries(chart, event) {
    const legendItem = /** @type {Element} */ (event.target).closest('.apexcharts-legend-series');
    if (!legendItem) return;

    const seriesIndex = Number(legendItem.getAttribute('rel')) - 1;
    // Highlight matches the first series with this name, which is the dashed half.
    [seriesIndex, pairedOverviewSeriesIndex(seriesIndex)].forEach((overviewSeriesIndex) => {
        const seriesElement = chart.el.querySelector(
            `.apexcharts-series[data\\:realIndex='${overviewSeriesIndex}']`,
        );
        seriesElement.classList.remove('legend-mouseover-inactive');
    });
}

/**
 * @param {import('apexcharts')} chart
 * @param {number} seriesIndex
 */
function togglePairedOverviewSeries(chart, seriesIndex) {
    const pairedSeriesIndex = pairedOverviewSeriesIndex(seriesIndex);
    const isHidden = chart.w.globals.collapsedSeriesIndices.includes(seriesIndex);

    // ApexCharts toggles the clicked series after this handler. Change the other half first so one update changes both.
    if (isHidden) {
        restoreCollapsedOverviewSeries(chart, pairedSeriesIndex);
        return;
    }

    chart.legend.legendHelpers.getSeriesAfterCollapsing({ realIndex: pairedSeriesIndex });
}

/**
 * @param {import('apexcharts')} chart
 * @param {number} seriesIndex
 */
function restoreCollapsedOverviewSeries(chart, seriesIndex) {
    const collapsedSeriesIndices = chart.w.globals.collapsedSeriesIndices;
    const collapsedPosition = collapsedSeriesIndices.indexOf(seriesIndex);
    const collapsedSeries = chart.w.globals.collapsedSeries[collapsedPosition];

    chart.w.config.series[seriesIndex].data = collapsedSeries.data.slice();
    chart.w.config.series[seriesIndex].hidden = false;
    chart.w.globals.collapsedSeries.splice(collapsedPosition, 1);
    collapsedSeriesIndices.splice(collapsedPosition, 1);
    chart.w.globals.risingSeries.push(seriesIndex);
}

/**
 * @param {number} seriesIndex
 * @returns {number}
 */
function pairedOverviewSeriesIndex(seriesIndex) {
    return seriesIndex % 2 === 0 ? seriesIndex + 1 : seriesIndex - 1;
}
